import React, { useEffect, useState } from 'react'
import Breadcrum from '../../components/breadcrum'
import { FaArrowLeft, FaEdit, FaEye, FaEyeSlash } from 'react-icons/fa'
import { Link, useNavigate, useParams } from 'react-router-dom'
import AddItem from '../../components/addItem'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  deleteCompany,
  deleteReport,
  getFinancialById,
  getFinancialReportsByYear,
  getHideReportByCompanyId,
  restoreReport,
  sendMail,
  sendMailCompany,
} from '../../api/financialApi'
import { getCleanPdfName, handleDate } from '../../handleDateAndPdf'
import Swal from 'sweetalert2'

const FinancialReport = () => {
  const { id } = useParams()
  const [selectedYear, setSelectedYear] = useState('')
  const navigate = useNavigate()
  const [showHidden, setShowHidden] = useState(false)
  const queryClient = useQueryClient()
  const [toDelete, setToDelete] = useState(null)
  const [sendingMail, setSendingMail] = useState(false)
  const [deletingCompany, setDeletingCompany] = useState(false)

  useEffect(() => {
    document.title = 'Financial Report'
  }, [])

  // Query for company basic info
  const {
    data: financialData,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
    error: companyError,
  } = useQuery({
    queryKey: ['financialData', id],
    queryFn: () => getFinancialById(id),
    enabled: !!id,
  })

  // Set default year when company data loads
  useEffect(() => {
    if (
      financialData?.financialYears &&
      financialData.financialYears.length > 0 &&
      !selectedYear
    ) {
      setSelectedYear(financialData.financialYears[0])
    }
  }, [financialData, selectedYear])

  // Show error toast for company data
  useEffect(() => {
    if (isCompanyError && companyError) {
      toast.error(
        companyError.response?.data?.message || 'Failed to fetch company data'
      )
    }
  }, [isCompanyError, companyError])

  // Query for financial reports by year
  const {
    data: yearlyReports,
    isLoading: isReportsLoading,
    isError: isReportsError,
    error: reportsError,
    refetch: refetchReports,
  } = useQuery({
    queryKey: ['financialReports', id, selectedYear, showHidden],
    queryFn: () =>
      showHidden
        ? getHideReportByCompanyId(id, selectedYear)
        : getFinancialReportsByYear(id, selectedYear),
    enabled: !!id && !!selectedYear,
  })

  // Show error toast for reports data
  useEffect(() => {
    if (isReportsError && reportsError) {
      toast.error(
        reportsError.response?.data?.message || 'Failed to fetch reports data'
      )
    }
  }, [isReportsError, reportsError])

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value)
  }

  // Send mail mutation with improved loading state
  const sendMailMutation = useMutation({
    mutationFn: (id) => (deletingCompany ? sendMailCompany(id) : sendMail(id)),
    onMutate: () => {
      setSendingMail(true)
    },
    onSuccess: (response) => {
      setSendingMail(false)

      // Check if the response indicates success
      if (response?.status === 200 || response?.data) {
        toast.success(response?.data)
        // Show OTP modal after successful email send
        showOTPModal()
      } else {
        toast.error('Failed to send verification email')
        setToDelete(null)
      }
    },
    onError: (err) => {
      setSendingMail(false)
      console.error('Send mail error:', err)
      toast.error(err.message || 'Failed to send verification email')
      setToDelete(null)
    },
  })

  // Delete mutation with improved success handling
  const deleteMutation = useMutation({
    mutationFn: ({ id, otp }) =>
      deletingCompany ? deleteCompany(id, otp) : deleteReport(id, otp),
    onSuccess: (response) => {
      // Invalidate and refetch financial
      queryClient.invalidateQueries({ queryKey: ['financialData'] })
      queryClient.invalidateQueries({ queryKey: ['financialReports'] })
      // Show success message
      toast.success('financial Hided successfully!')
      // Additional success message with SweetAlert
      Swal.fire({
        title: 'Hide!',
        text: 'The financial has been hiding successfully.',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        timer: 3000,
        timerProgressBar: true,
      })

      // Reset state
      setToDelete(null)
      deleteCompany && navigate(`/dealdashboard/financial`)
    },
    onError: (err) => {
      console.error('Hide error:', err)
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to hide financial. Please check your OTP.'
      toast.error(errorMessage)
    },
  })

  // Show OTP modal using SweetAlert with better UX
  const showOTPModal = () => {
    Swal.fire({
      title: 'Enter OTP',
      html: `
          <p>Please enter the OTP sent to your email</p>
          <div style="margin: 10px 0; padding: 10px; background-color: #f0f9ff; border-radius: 8px; border-left: 4px solid #3b82f6;">
            <small style="color: #1e40af;">
              <i class="fas fa-info-circle"></i> Check your email inbox and spam folder
            </small>
          </div>
        `,
      input: 'number',
      inputAttributes: {
        autocapitalize: 'off',
        placeholder: 'Enter 6-digit OTP',
        maxlength: 6,
        style: 'text-align: center; font-size: 18px; letter-spacing: 2px;',
      },
      showCancelButton: true,
      confirmButtonText: 'Hide financial',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      allowOutsideClick: false,
      inputValidator: (value) => {
        if (!value) {
          return 'Please enter OTP'
        }
        if (value.length !== 6) {
          return 'OTP must be 6 digits'
        }
        if (!/^\d+$/.test(value)) {
          return 'OTP must contain only numbers'
        }
      },
      preConfirm: (otp) => {
        return new Promise((resolve, reject) => {
          // Show loading state
          Swal.showLoading()

          // Update confirm button text while loading
          const confirmButton = Swal.getConfirmButton()
          const originalText = confirmButton.textContent
          confirmButton.textContent = 'Hiding...'

          // Call delete mutation with OTP
          deleteMutation.mutate(
            { id: toDelete, otp },
            {
              onSuccess: () => {
                resolve()
                Swal.close()
              },
              onError: (error) => {
                confirmButton.textContent = originalText
                Swal.hideLoading()
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.message ||
                  'Invalid OTP or hide failed'
                Swal.showValidationMessage(`❌ ${errorMessage}`)
                reject(error)
              },
            }
          )
        })
      },
    })
      .then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          setToDelete(null)
          toast.info('Hiding cancelled')
        }
      })
      .catch(() => {
        // Handle any unexpected errors
        setToDelete(null)
      })
  }

  // Handle delete click with improved UX
  const handleDeleteClick = (financialId, financialTitle) => {
    setToDelete(financialId)

    // Show confirmation dialog first
    Swal.fire({
      title: 'Hide financial?',
      html: `
          <p>Are you sure you want to hide this financial?</p>
          <div style="margin: 10px 0; padding: 10px; background-color: #fef2f2; border-radius: 8px; border-left: 4px solid #ef4444;">
            <strong style="color: #dc2626;">${financialTitle}</strong>
          </div>
          <p style="color: #6b7280; font-size: 14px;">
            This action will send an OTP to your email for verification.
          </p>
        `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Send OTP',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        // Send email for OTP
        sendMailMutation.mutate(financialId)
      } else {
        setToDelete(null)
      }
    })
  }

  const restoreMutation = useMutation({
    mutationFn: (id) => restoreReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financialData'] })
      queryClient.invalidateQueries({ queryKey: ['financialReports'] })
      toast.success('Report restore sucessfully!')
    },
    onError: (err) => {
      console.error('restore report error:', err)
      toast.error(err.message || 'Failed to send verification email')
    },
  })

  if (isCompanyLoading) {
    return (
      <>
        <Breadcrum heading="Insight" subheading="Financial report" />
        <div className="text-center py-8">Loading company info...</div>
      </>
    )
  }

  if (isCompanyError) {
    return (
      <>
        <Breadcrum heading="Insight" subheading="Financial report" />
        <div className="text-center py-8 text-red-500">
          Error loading company information. Please try again later.
        </div>
      </>
    )
  }

  const handleToggle = () => {
    setShowHidden((prev) => !prev)
  }

  return (
    <>
      <Breadcrum subheading="Financial report" heading="Insight" />

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex justify-center items-center bg-gray-500 p-2 text-white rounded sticky -top-[15px]"
      >
        <FaArrowLeft />
        &nbsp; Back
      </button>

      {financialData && (
        <div className="md:w-[90%] mx-auto my-6 p-6 bg-white shadow-md rounded-lg">
          <div className="flex items-center mb-4">
            <img
              src={financialData.companyLogoUrl}
              alt="company logo"
              className="w-16 h-16 object-contain mr-4"
            />
            <div>
              <h3 className="text-xl font-semibold">
                {financialData.companyName}
              </h3>
              <h4 className="text-gray-600">CIN: {financialData.cin}</h4>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-700">
                <strong>Created By:</strong> {financialData?.createAdmin}
              </p>
              <p className="text-gray-700">
                <strong>Created At:</strong>{' '}
                {handleDate(financialData.createdAt)}
              </p>
            </div>

            {financialData.updateAdmin && (
              <div>
                <p className="text-gray-700">
                  <strong>Updated By:</strong> {financialData?.updateAdmin}
                </p>
                <p className="text-gray-700">
                  <strong>Updated At:</strong>{' '}
                  {handleDate(financialData.updatedAt)}
                </p>
              </div>
            )}
          </div>

          {financialData?.financialYears &&
            financialData.financialYears.length > 0 && (
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Select Financial Year:
                </label>
                <select
                  className="border border-gray-300 rounded-md px-3 py-2 min-w-[200px]"
                  value={selectedYear}
                  onChange={handleYearChange}
                >
                  <option value="">Select Year</option>
                  {financialData.financialYears.map((year, index) => (
                    <option key={index} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            )}

          <div className="flex justify-start items-center gap-5">
            <Link
              to={`/dealdashboard/financial/edit/company/${financialData.companyId}`}
              className="flex justify-center items-center bg-orange-500 text-white p-2 rounded hover:bg-orange-600 max-w-[100px]"
              title="Edit"
            >
              <FaEdit />
            </Link>
            <button
              type="button"
              onClick={() => {
                handleDeleteClick(
                  financialData.companyId,
                  financialData.companyName
                )
                setDeletingCompany(true)
              }}
              className={`text-white bg-red-500 p-2 rounded hover:bg-red-700 transition-colors ${
                sendingMail || deleteMutation.isPending
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
              title="Hide"
              disabled={sendingMail || deleteMutation.isPending}
            >
              {sendingMail && toDelete === financialData.companyId ? (
                <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
              ) : (
                <FaEyeSlash />
              )}
            </button>
          </div>
        </div>
      )}

      <div className="md:w-[90%] mx-auto flex justify-between items-center">
        <div>
          <h2
            className={`text-2xl font-semibold ${
              showHidden ? 'text-red-600' : 'text-gray-600'
            }`}
          >
            {showHidden ? 'Hidden' : 'Active'} Files{' '}
            {selectedYear && `- ${selectedYear}`}
          </h2>
          {selectedYear && (
            <p className="text-gray-500 mt-1">
              Showing reports for financial year: {selectedYear}
            </p>
          )}
        </div>

        <button
          onClick={handleToggle}
          title={showHidden ? 'Show active reports' : 'Show hidden reports'}
          className="text-white bg-blue-500 p-2 rounded hover:bg-blue-700 transition-colors"
        >
          {showHidden ? <FaEye /> : <FaEyeSlash />}
        </button>
      </div>

      <div className="md:w-[90%] mx-auto my-6 overflow-x-auto whitespace-nowrap">
        <table className="min-w-full border border-gray-200 text-center shadow-md rounded-lg">
          <thead className="bg-[#E9ECEF] text-gray-700">
            <tr>
              <th className="py-3 px-4 border-b">S no.</th>
              <th className="py-3 px-4 border-b">Title</th>
              <th className="py-3 px-4 border-b">Description</th>
              <th className="py-3 px-4 border-b">Pdf</th>
              <th className="py-3 px-4 border-b">Price</th>
              <th className="py-3 px-4 border-b">Created At</th>
              <th className="py-3 px-4 border-b">Created By</th>
              <th className="py-3 px-4 border-b">Updated At</th>
              <th className="py-3 px-4 border-b">Updated By</th>
              <th className="py-3 px-4 border-b">Action</th>
            </tr>
          </thead>
          <tbody>
            {isReportsLoading ? (
              <tr>
                <td colSpan="7" className="py-8 text-center text-gray-500">
                  Loading reports for {selectedYear}...
                </td>
              </tr>
            ) : isReportsError ? (
              <tr>
                <td colSpan="7" className="py-8 text-center text-red-500">
                  Error loading reports. Please try again.
                </td>
              </tr>
            ) : !yearlyReports || yearlyReports.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-8 text-center text-gray-500">
                  {selectedYear
                    ? `No reports found for ${selectedYear}`
                    : 'Please select a financial year'}
                </td>
              </tr>
            ) : (
              yearlyReports &&
              yearlyReports.map((item, index) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 text-gray-700 font-light transition-colors duration-200"
                >
                  <td className="py-3 px-4 border-b font-medium">
                    {index + 1}
                  </td>
                  <td className="py-3 px-4 border-b font-medium max-w-md">
                    <div className="truncate" title={item.reportTitle}>
                      {item.reportTitle}
                    </div>
                  </td>
                  <td className="py-3 px-4 border-b font-medium max-w-lg">
                    <div className="truncate" title={item.reportDesc}>
                      {item.reportDesc}
                    </div>
                  </td>

                  <td className="py-3 px-4 border-b font-medium">
                    {getCleanPdfName(item.reportUrl)}
                  </td>

                  <td className="py-3 px-4 border-b font-medium">
                    &#8377;{item.reportPrice}
                  </td>
                  <td className="py-3 px-4 border-b font-medium">
                    {handleDate(item.createdAt)}
                  </td>
                  <td className="py-3 px-4 border-b font-medium">
                    {item?.createAdmin}
                  </td>
                  <td className="py-3 px-4 border-b font-medium">
                    {item?.updatedAt ? handleDate(item.updatedAt) : 'null'}
                  </td>
                  <td className="py-3 px-4 border-b font-medium">
                    {item?.updateAdmin ? item.updateAdmin : 'null'}
                  </td>
                  <td className="py-3 px-4 border-b">
                    <div className="flex justify-center space-x-2">
                      {item?.isDeleted === false && (
                        <Link
                          to={`/dealdashboard/edit/financial/${id}/${item.id}`}
                          className="bg-orange-400 text-white p-2 rounded-md hover:bg-orange-500"
                          title="Edit"
                        >
                          <FaEdit />
                        </Link>
                      )}
                      {item?.isDeleted === true ? (
                        <button
                          title="Restore report"
                          onClick={() => restoreMutation.mutate(item.id)}
                          className="text-white bg-green-700 p-2 rounded hover:bg-green-900 transition-colors"
                        >
                          <FaEye />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            handleDeleteClick(
                              item.id,
                              getCleanPdfName(item.reportUrl)
                            )
                          }
                          className={`text-white bg-red-500 p-2 rounded hover:bg-red-700 transition-colors ${
                            sendingMail || deleteMutation.isPending
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                          title="Hide"
                          disabled={sendingMail || deleteMutation.isPending}
                        >
                          {sendingMail && toDelete === item.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                          ) : (
                            <FaEyeSlash />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Loading overlay for sending email */}
        {sendingMail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl text-center">
              <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-lg font-semibold">
                Sending verification email...
              </p>
              <p className="text-sm text-gray-500 mt-1">Please wait</p>
            </div>
          </div>
        )}
      </div>
      {showHidden !== true && (
        <AddItem redirect={`/dealdashboard/add/financial/${id}`} />
      )}
    </>
  )
}

export default FinancialReport
